import { css, html, LitElement, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
// import { ApiBlockListModel, ContentApi, OpenAPI, PageContentResponseModel } from "./api"
import { renderElement } from './RenderElement'

import './Elements/Grid'
import './Elements/Hero'
import './Elements/MediaBlock'
import './Elements/Slider'
import './Elements/TextBlock'
import './Elements/TextArea'
import './Footer/Footer'
import './Header/Header'
import './Elements/Style'
import './Elements/Layouts'
import './HtmlTest'
import './Dealers/Dealers'
import './Debug'
import { ApiBlockListModel, SeoValues } from './api/ContentDeliveryApi.schemas'
import type { UmbracoPage } from './ContentLoader'
import { getContent } from './ContentLoader'
import { CONTENT_BASE } from './Settings'

@customElement('lindberg-website')
export class Website extends LitElement {
	@state()
	root!: UmbracoPage
	
	@state()
	currentPage!: UmbracoPage

	@state()
	pages: UmbracoPage[] = []

	@state()
	breadcrumb: UmbracoPage[]= []

	@state()
	sections: ApiBlockListModel = {items: []}

	static styles = css`
		:host {
			@media (width < 600px) { --breakpoint: X-Small }
			@media (600px <= width < 912px) { --breakpoint: Small }
			@media (912px <= width < 1160px) { --breakpoint: Medium }
			@media (width >= 1160px) { --breakpoint: Large }

			--min-page-width: 320px;
			--max-page-width: 1920px;
			--page-width: clamp(var(--min-page-width), 100%, var(--max-page-width));
			
			display: flex;
			flex-direction: column;
			width: 100%;
			min-height: 100vh;
			/* gap: var(--grid-gutter); */
			overflow-x: hidden;
			align-items: center;

			& > * {
				width: clamp(var(--min-page-width), calc(100% - (2 * var(--grid-gutter))), var(--max-page-width));
				margin-bottom: var(--grid-gutter);
			}

			& > lindberg-header, & > lindberg-footer {
				width: var(--page-width);
				margin: 0;
			}
			@media (width < 600px) {
				& > lindberg-product-view {
					width: var(--page-width);
					margin: 0;
				}
			}
			
			& > lindberg-footer {
				margin-top: auto;
			}
		}
    `

	async connectedCallback() {
		super.connectedCallback()
		document.documentElement.classList.toggle('color-scheme', localStorage.getItem('color-scheme') === 'true')
		const response = await fetch(`${CONTENT_BASE}/umbraco/delivery/api/v2/extended/item-by-route` + document.location.pathname)
		const {item, root, redirect} = await response.json()
		console.log({item, root, redirect})
		if(!response.ok && response.status === 404) {
			if(root.properties?.default404Page) {
				window.location.replace(root.properties.default404Page.route.path)
				return
			}
		}else if(redirect) {
			window.location.replace(redirect.destination)
			return
		}

		this.root = root
		this.currentPage = item

		await this.loadCustomiserComponents();
		
		if (this.currentPage) {
			this.sections = this.currentPage.properties?.sections || {items: []}
		}
		this.updateMetadata()
		// console.log(this.currentPage)
	}

	updateMetadata() {
		if(this.currentPage){
			const { path } = this.currentPage.route;
			const currentCulture = Object.entries((this.currentPage as any).cultures) as [string, any][];
			const currentCultureKey = currentCulture.find(([, culture]) => culture.path === path)?.[0];
			if(currentCultureKey) document.documentElement.lang = currentCultureKey
			const seo = this.currentPage.properties?.seo as SeoValues
			if(seo?.title) document.title = seo.title
			if(seo?.description) {
				let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]')
				if (!metaDesc) {
					metaDesc = document.createElement('meta')
					metaDesc.name = 'description'
					document.head.appendChild(metaDesc)
				}
				metaDesc.setAttribute('content', seo.description)
			}
		}
		
		if(this.root.properties.html) {
			const html = this.root.properties.html as string
			const temp = document.createElement('div')
			temp.innerHTML = html

			temp.querySelectorAll('script').forEach(oldScript => {
				const newScript = document.createElement('script')
				Array.from(oldScript.attributes).forEach(attr =>
					newScript.setAttribute(attr.name, attr.value)
				)
				newScript.textContent = oldScript.textContent
				document.head.appendChild(newScript)
			})

			// append any remaining non-script nodes if needed
			Array.from(temp.childNodes).forEach(node => {
				if (node.nodeName !== 'SCRIPT') document.head.appendChild(node)
			})
		}
	}

	async loadCustomiserComponents() {
		const componentFolder = import.meta.env[`VITE_COMPONENT_CUSTOMISER_COMPONENTS`];
		const manifestJson = `${componentFolder}manifest.json`;

		let mainSourceFile = `${componentFolder}main.js`;

		try {
			const manifestResponse = await fetch(manifestJson);
			const manifest = await manifestResponse.json();

			if (manifest.main) {
				// If the manifest.json contains a main field, use that to load the javascript.
				mainSourceFile = `${componentFolder}${manifest.main}`
			}
		}
		catch {
			// manifest.json could not be loaded (or contained invalid data)
			// Just ignore and use {componentFolder}main.js as a fallback (mostly for dockerlocal)
		}

		const scriptElement = document.createElement('script');
		scriptElement.type = 'module';
		scriptElement.src = mainSourceFile;

		document.head.append(scriptElement);
	}

	render() {
		if(!this.root) return nothing
		return html`
        <lindberg-header .root=${this.root} .currentPage=${this.currentPage}></lindberg-header>
        ${this.sections ? this.sections.items.map(x => renderElement(x, this.root)) : ''}
        <lindberg-footer .root=${this.root} .currentPage=${this.currentPage}></lindberg-footer>
        `
	}
}